import { useEffect, useRef, useState } from 'react';
import { pricingCategories, type PricingPlan, type CategoryFaq } from '../../../mocks/pricing';
import ServiceOrderModal from './ServiceOrderModal';

function useInView() {
  const ref = useRef<HTMLDivElement>(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) setInView(true); },
      { threshold: 0.08 }
    );
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);
  return { ref, inView };
}

// Maps pricing category IDs to service IDs from services.ts
const CATEGORY_SERVICE_MAP: Record<string, number> = {
  weby: 1,
  eshopy: 2,
  marketing: 3,
  socialne: 4,
  sprava: 5,
  crm: 6,
  automatizacie: 7,
};

interface ModalState {
  open: boolean;
  serviceId: number;
  planName: string;
  planPrice: string;
}

export default function PricingSection() {
  const { ref, inView } = useInView();
  const [activeId, setActiveId] = useState(pricingCategories[0].id);
  const [animating, setAnimating] = useState(false);
  const [modal, setModal] = useState<ModalState>({
    open: false,
    serviceId: 1,
    planName: '',
    planPrice: '',
  });

  const active = pricingCategories.find((c) => c.id === activeId)!;

  useEffect(() => {
    const handler = (e: Event) => {
      const categoryId = (e as CustomEvent<{ categoryId: string }>).detail?.categoryId;
      if (categoryId) switchCategory(categoryId);
    };
    window.addEventListener('pricing-switch', handler);
    return () => window.removeEventListener('pricing-switch', handler);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeId]);

  const switchCategory = (id: string) => {
    if (id === activeId) return;
    setAnimating(true);
    setTimeout(() => {
      setActiveId(id);
      setAnimating(false);
    }, 180);
  };

  const handleOrder = (plan: PricingPlan) => {
    setModal({
      open: true,
      serviceId: CATEGORY_SERVICE_MAP[activeId] ?? 1,
      planName: plan.name,
      planPrice: plan.price,
    });
  };

  const handleScrollKontakt = () => {
    const el = document.querySelector('#kontakt');
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section id="cennik" className="py-28 bg-zinc-950">
      <div className="max-w-7xl mx-auto px-6">

        {/* Header */}
        <div
          ref={ref}
          className={`text-center mb-14 transition-all duration-700 ${inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
        >
          <div className="inline-flex items-center gap-2 bg-orange-500/10 border border-orange-500/20 rounded-full px-4 py-1.5 mb-6">
            <i className="ri-price-tag-3-line text-orange-400 text-sm" />
            <span className="text-orange-300 text-sm font-medium">Transparentné ceny</span>
          </div>
          <h2 className="text-4xl md:text-5xl font-black text-white leading-tight mb-5">
            Investícia, ktorá sa{' '}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-orange-400 to-amber-400">
              vráti
            </span>
          </h2>
          <p className="text-zinc-400 text-lg max-w-xl mx-auto">
            Žiadne skryté poplatky. Vyberajte podľa vašich potrieb - vždy vieme navrhnúť riešenie na mieru.
          </p>
        </div>

        {/* Category tabs */}
        <div
          className={`mb-10 transition-all duration-700 delay-100 ${inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
        >
          <div className="flex overflow-x-auto gap-2 pb-2 scrollbar-hide justify-start md:justify-center">
            {pricingCategories.map((cat) => (
              <button
                key={cat.id}
                onClick={() => switchCategory(cat.id)}
                className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold whitespace-nowrap transition-all duration-200 cursor-pointer flex-shrink-0 ${
                  activeId === cat.id
                    ? 'bg-orange-500 text-white'
                    : 'bg-zinc-900 border border-zinc-800 text-zinc-400 hover:text-white hover:border-zinc-600'
                }`}
              >
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className={cat.icon} />
                </div>
                {cat.label}
              </button>
            ))}
          </div>
        </div>

        {/* Subtitle note (above cards) */}
        {active.note && active.noteStyle === 'subtitle' && (
          <div className={`mb-8 text-center transition-all duration-300 ${animating ? 'opacity-0' : 'opacity-100'}`}>
            <p className="text-zinc-400 text-base max-w-2xl mx-auto leading-relaxed">
              {active.note}
            </p>
          </div>
        )}

        {/* Cards */}
        <div
          className={`grid grid-cols-1 md:grid-cols-3 gap-6 transition-all duration-300 ${
            animating ? 'opacity-0 translate-y-3 scale-[0.99]' : 'opacity-100 translate-y-0 scale-100'
          }`}
        >
          {active.plans.map((plan, i) => (
            <PricingCard key={`${activeId}-${i}`} plan={plan} onOrder={handleOrder} />
          ))}
        </div>

        {/* Info note (below cards) */}
        {active.note && active.noteStyle !== 'subtitle' && (
          <div className={`mt-5 transition-all duration-300 ${animating ? 'opacity-0' : 'opacity-100'}`}>
            <div className="flex items-start gap-2.5 bg-zinc-900 border border-zinc-800 rounded-xl px-4 py-3 max-w-2xl mx-auto">
              <div className="w-4 h-4 flex items-center justify-center flex-shrink-0 mt-0.5">
                <i className="ri-information-line text-orange-400 text-sm" />
              </div>
              <p className="text-zinc-400 text-sm leading-snug">
                {active.note}
              </p>
            </div>
          </div>
        )}

        {/* Category-specific FAQ */}
        {active.faqs && active.faqs.length > 0 && (
          <div className={`mt-16 transition-all duration-300 ${animating ? 'opacity-0 translate-y-3' : 'opacity-100 translate-y-0'}`}>
            <CategoryFaqBlock faqs={active.faqs} categoryLabel={active.label} />
          </div>
        )}

        {/* Bottom note */}
        <div
          className={`mt-12 text-center transition-all duration-700 delay-300 ${inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
        >
          <p className="text-zinc-600 text-sm mb-4">
            Nie ste si istí, ktorý plán je pre vás? Poradíme vám zadarmo.
          </p>
          <button
            onClick={handleScrollKontakt}
            className="inline-flex items-center gap-2 text-orange-400 hover:text-orange-300 font-semibold text-sm transition-colors duration-200 cursor-pointer whitespace-nowrap"
          >
            Dohodniť bezplatnú konzultáciu
            <i className="ri-arrow-right-line" />
          </button>
        </div>
      </div>

      {/* Order Modal */}
      {modal.open && (
        <ServiceOrderModal
          selectedServiceId={modal.serviceId}
          planName={modal.planName}
          planPrice={modal.planPrice}
          onClose={() => setModal((prev) => ({ ...prev, open: false }))}
        />
      )}
    </section>
  );
}

function CategoryFaqBlock({ faqs, categoryLabel }: { faqs: CategoryFaq[]; categoryLabel: string }) {
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  return (
    <div className="max-w-4xl mx-auto">
      {/* Header */}
      <div className="flex items-center gap-3 mb-8">
        <div className="w-10 h-10 flex items-center justify-center bg-orange-500/15 rounded-xl flex-shrink-0">
          <i className="ri-question-answer-line text-orange-400 text-lg" />
        </div>
        <div>
          <h3 className="text-white font-bold text-xl leading-tight">
            Časté otázky — {categoryLabel}
          </h3>
          <p className="text-zinc-500 text-sm mt-0.5">Všetko čo potrebujete vedieť pred začatím</p>
        </div>
      </div>

      {/* Two-column grid on desktop */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {faqs.map((faq, i) => {
          const isOpen = openIndex === i;
          return (
            <div
              key={i}
              onClick={() => setOpenIndex(isOpen ? null : i)}
              className={`rounded-2xl border cursor-pointer transition-all duration-250 ${
                isOpen
                  ? 'bg-orange-500/8 border-orange-500/30'
                  : 'bg-zinc-900 border-zinc-800 hover:border-zinc-700'
              }`}
            >
              {/* Question row */}
              <div className="flex items-start gap-3 p-5">
                <div className={`w-7 h-7 flex items-center justify-center rounded-lg flex-shrink-0 mt-0.5 transition-all duration-200 ${
                  isOpen ? 'bg-orange-500 rotate-45' : 'bg-zinc-800'
                }`}>
                  <i className={`ri-add-line text-sm transition-colors duration-200 ${isOpen ? 'text-white' : 'text-zinc-400'}`} />
                </div>
                <p className={`text-sm font-semibold leading-snug transition-colors duration-200 ${
                  isOpen ? 'text-orange-300' : 'text-zinc-200'
                }`}>
                  {faq.question}
                </p>
              </div>

              {/* Answer */}
              <div className={`overflow-hidden transition-all duration-300 ease-in-out ${isOpen ? 'max-h-64 opacity-100' : 'max-h-0 opacity-0'}`}>
                <div className="px-5 pb-5 pt-0 pl-[60px]">
                  <p className="text-zinc-400 text-sm leading-relaxed">{faq.answer}</p>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Bottom CTA strip */}
      <div className="mt-6 flex flex-col sm:flex-row items-center gap-4 bg-zinc-900 border border-zinc-800 rounded-2xl px-6 py-4">
        <div className="w-9 h-9 flex items-center justify-center bg-orange-500/15 rounded-xl flex-shrink-0">
          <i className="ri-robot-2-line text-orange-400" />
        </div>
        <p className="text-zinc-400 text-sm text-center sm:text-left">
          Máte špecifický prípad? Radi zanalyzujeme vaše procesy a navrhneme riešenie na mieru — <span className="text-zinc-200 font-semibold">konzultácia zadarmo</span>.
        </p>
        <button
          onClick={() => { const el = document.querySelector('#kontakt'); if (el) el.scrollIntoView({ behavior: 'smooth' }); }}
          className="flex-shrink-0 bg-orange-500 hover:bg-orange-400 text-white text-sm font-bold px-5 py-2.5 rounded-xl transition-all duration-200 cursor-pointer whitespace-nowrap sm:ml-auto"
        >
          Konzultácia zadarmo
        </button>
      </div>
    </div>
  );
}

function PricingCard({ plan, onOrder }: { plan: PricingPlan; onOrder: (plan: PricingPlan) => void }) {
  return (
    <div
      className={`relative flex flex-col rounded-2xl p-7 transition-all duration-300 hover:-translate-y-1 ${
        plan.popular
          ? 'bg-gradient-to-b from-orange-500/10 to-zinc-900 border-2 border-orange-500/50'
          : 'bg-zinc-900 border border-zinc-800 hover:border-zinc-700'
      }`}
    >
      {/* Popular badge */}
      {plan.popular && (
        <div className="absolute -top-3.5 left-1/2 -translate-x-1/2">
          <div className="flex items-center gap-1.5 bg-gradient-to-r from-orange-500 to-amber-500 text-white text-xs font-bold px-4 py-1.5 rounded-full whitespace-nowrap">
            {plan.popularBadge ?? '⭐ Najpopulárnejší'}
          </div>
        </div>
      )}

      {/* Plan name + target audience + price */}
      <div className="mb-6">
        <h3 className={`text-lg font-bold mb-2 ${plan.popular ? 'text-orange-300' : 'text-zinc-300'}`}>
          {plan.name}
        </h3>
        
        {/* Target audience tag */}
        {plan.targetAudience && (
          <div className="mb-4">
            <span className={`inline-block text-xs font-medium px-2.5 py-1 rounded-full ${
              plan.popular 
                ? 'bg-orange-500/20 text-orange-300 border border-orange-500/30' 
                : 'bg-zinc-800 text-zinc-400 border border-zinc-700'
            }`}>
              {plan.targetAudience}
            </span>
          </div>
        )}
        
        <div className="flex items-end gap-2">
          <span className="text-4xl font-black text-white leading-none">{plan.price}</span>
        </div>
        <p className="text-zinc-500 text-sm mt-1.5">{plan.billing}</p>

        {plan.budgetNote && (
          <div className="mt-4 bg-zinc-800/60 border border-zinc-700/50 rounded-lg px-3.5 py-2.5 flex items-start gap-2">
            <div className="w-4 h-4 flex items-center justify-center flex-shrink-0 mt-0.5">
              <i className="ri-information-line text-orange-400 text-xs" />
            </div>
            <p className="text-zinc-400 text-xs leading-snug">{plan.budgetNote}</p>
          </div>
        )}
      </div>

      {/* Divider */}
      <div className={`w-full h-px mb-6 ${plan.popular ? 'bg-orange-500/20' : 'bg-zinc-800'}`} />

      {/* Features */}
      <ul className="space-y-3 flex-1 mb-8">
        {plan.features.map((feature, idx) => (
          <li key={idx} className="flex items-start gap-3">
            <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
              <i className={`ri-check-line text-sm font-bold ${plan.popular ? 'text-orange-400' : 'text-emerald-400'}`} />
            </div>
            <span className="text-zinc-300 text-sm leading-snug">{feature}</span>
          </li>
        ))}
      </ul>

      {/* CTA */}
      <button
        onClick={() => onOrder(plan)}
        className={`w-full py-3.5 rounded-xl font-bold text-sm transition-all duration-200 cursor-pointer whitespace-nowrap flex items-center justify-center gap-2 ${
          plan.popular
            ? 'bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-400 hover:to-amber-400 text-white'
            : 'bg-zinc-800 hover:bg-zinc-700 text-white border border-zinc-700 hover:border-zinc-600'
        }`}
      >
        {plan.cta}
        <i className="ri-arrow-right-line" />
      </button>
    </div>
  );
}
